import type { Article } from "./types";

export const articlesEs: Record<string, Article> = {
  "what-is-basetao-spreadsheet": {
    slug: "what-is-basetao-spreadsheet",
    title: "¿Qué es Basetao Spreadsheet? Guía completa para principiantes",
    description:
      "Descubre qué es Basetao Spreadsheet, cómo funciona y cómo usarlo para encontrar productos, comparar vendedores y planificar envío y QC antes de pedir.",
    intro: [
      "Un Basetao Spreadsheet es una base de datos de productos organizada que ayuda a los compradores a encontrar y comparar artículos disponibles a través del agente de compras Basetao. En lugar de desplazarse por miles de anuncios individuales, puedes navegar por un único documento estructurado que agrupa los productos por categoría e incluye detalles de apoyo como información del vendedor, referencias de productos y notas prácticas.",
      "Esta guía explica qué es el Basetao Spreadsheet, qué suele contener, cómo usarlo de forma eficaz y qué tener en cuenta antes de hacer un pedido. Está pensada para principiantes que quieren una visión clara y práctica sin relleno promocional.",
    ],
    takeaways: [
      "El Basetao Spreadsheet agrupa los productos en categorías claras como zapatos, sudaderas, camisetas, chaquetas y accesorios.",
      "Es una ayuda de investigación que suele incluir detalles del vendedor, referencias de productos y organización por categorías.",
      "Verifica siempre los precios, el stock y la reputación del vendedor en la plataforma oficial antes de pedir.",
      "Los costes de envío, las fotos QC y las políticas de almacén varían, así que confirma las condiciones actuales.",
    ],
    sections: [
      {
        heading: "¿Qué es Basetao Spreadsheet?",
        paragraphs: [
          "Basetao es un agente de compras que ayuda a los compradores internacionales a adquirir productos de mercados chinos. Ofrece servicios como realización de pedidos, inspección de calidad, almacenamiento, consolidación y envío internacional. Como estos mercados contienen una enorme cantidad de anuncios, encontrar productos fiables puede llevar mucho tiempo.",
          "El Basetao Spreadsheet se creó para facilitar este proceso. Es una lista de productos y referencias organizada por categorías que los compradores pueden usar para investigar lo disponible. Piénsalo como un mapa: no vende nada por sí mismo, pero te ayuda a entender qué productos existen, cómo se agrupan y dónde buscar a continuación.",
        ],
      },
      {
        heading: "¿Qué contiene el Spreadsheet?",
        paragraphs: [
          "El contenido exacto puede variar, pero un Basetao Spreadsheet típico incluye varias piezas útiles organizadas para un escaneo rápido.",
        ],
        items: [
          "Categorías — productos agrupados por tipo como zapatos, sudaderas, camisetas, chaquetas, pantalones, gorros, bolsos, cinturones y accesorios.",
          "Referencias de productos — identificadores o nombres que te ayudan a localizar un artículo concreto.",
          "Información del vendedor — detalles que ayudan a comparar distintos vendedores.",
          "Notas prácticas — consejos sobre tallas, calidad y qué vigilar antes de pedir.",
        ],
      },
      {
        heading: "Cómo usar el Spreadsheet paso a paso",
        paragraphs: [
          "Usar el spreadsheet es sencillo, pero seguir un proceso simple te ayuda a evitar errores comunes.",
        ],
        items: [
          "Abre el spreadsheet y revisa la lista de categorías.",
          "Elige una categoría que coincida con lo que buscas.",
          "Anota las referencias de productos que quieres investigar.",
          "Compara la información del vendedor y los detalles del producto.",
          "Comprueba los requisitos de envío y QC para tu destino.",
          "Verifica los precios y la disponibilidad en Basetao antes de pedir.",
        ],
      },
      {
        heading: "Categorías de productos que puedes explorar",
        paragraphs: [
          "El spreadsheet está organizado en torno a categorías de compra comunes que te ayudan a acotar rápidamente tu búsqueda.",
        ],
        items: [
          "Zapatos",
          "Sudaderas y jerséis",
          "Camisetas",
          "Chaquetas",
          "Pantalones y pantalones cortos",
          "Gorros",
          "Conjuntos",
          "Ropa interior",
          "Camisetas deportivas",
          "Accesorios",
          "Bolsos",
          "Cinturones",
          "Perfumes",
        ],
      },
      {
        heading: "Beneficios y limitaciones",
        paragraphs: [
          "Como cualquier herramienta de investigación, el spreadsheet tiene puntos fuertes claros y algunas cosas que no puede hacer.",
        ],
        items: [
          "Ahorra tiempo frente a buscar manualmente entre miles de anuncios.",
          "Ofrece una estructura de categorías clara y fácil de navegar.",
          "Ayuda a comparar vendedores y descubrir productos.",
          "No garantiza disponibilidad ni precio — los detalles pueden cambiar.",
          "La información puede quedar obsoleta, verifica antes de pedir.",
          "No sustituye la verificación oficial en la plataforma Basetao.",
        ],
      },
      {
        heading: "Envío, almacén y QC",
        paragraphs: [
          "Los costes de envío dependen de factores como el peso, el volumen, el destino y el método elegido. El almacén de Basetao guarda los artículos tras la compra, y las fotos de control de calidad (QC) te ayudan a confirmar que un producto se ve correcto antes de enviarlo internacionalmente. Como estos detalles cambian, comprueba siempre las condiciones actuales en la plataforma oficial.",
        ],
      },
    ],
    faq: [
      {
        q: "¿Qué es Basetao Spreadsheet?",
        a: "Es una lista organizada por categorías de productos y referencias que ayuda a los compradores a investigar artículos disponibles a través del agente de compras Basetao.",
      },
      {
        q: "¿Es gratis el Basetao Spreadsheet?",
        a: "La disponibilidad y el precio pueden variar, así que consulta la fuente de la que lo obtuviste para la información más actual.",
      },
      {
        q: "¿Sigo necesitando verificar los productos en Basetao?",
        a: "Sí. Un spreadsheet es una ayuda de investigación, no una garantía. Confirma siempre precios, stock y detalles del vendedor en la plataforma oficial antes de pedir.",
      },
      {
        q: "¿Qué categorías están incluidas?",
        a: "Las categorías comunes incluyen zapatos, sudaderas y jerséis, camisetas, chaquetas, pantalones y cortos, gorros, conjuntos, ropa interior, camisetas deportivas, accesorios, bolsos, cinturones y perfumes.",
      },
    ],
    conclusion:
      "El Basetao Spreadsheet es un punto de partida práctico para cualquiera que quiera investigar productos disponibles a través de Basetao sin excavar manualmente entre anuncios interminables. Al organizar los productos en categorías claras y emparejarlos con referencias útiles, ahorra tiempo y ayuda a comparar opciones. Trátalo como una guía, no como una garantía, y verifica siempre los detalles actuales antes de hacer un pedido.",
  },

  "how-to-use-basetao-spreadsheet": {
    slug: "how-to-use-basetao-spreadsheet",
    title: "Cómo usar Basetao Spreadsheet: guía paso a paso",
    description:
      "Aprende a usar el Basetao Spreadsheet paso a paso para explorar categorías, comparar vendedores, planificar el envío y hacer tu primer pedido con confianza.",
    intro: [
      "Empezar con el Basetao Spreadsheet es más fácil de lo que parece, pero un proceso claro marca la diferencia entre un pedido fluido y una experiencia frustrante. Esta guía te acompaña por cada etapa, desde abrir el spreadsheet hasta confirmar tu pedido.",
      "No necesitas herramientas especiales ni conocimientos técnicos. Un poco de paciencia y atención al detalle bastan para investigar productos con eficacia y evitar los errores más comunes de principiante.",
    ],
    takeaways: [
      "Empieza revisando las categorías y luego acota al tipo de producto que quieres.",
      "Compara los detalles del vendedor y las referencias antes de comprometerte.",
      "Planifica el envío y el QC con antelación para evitar sorpresas.",
      "Verifica siempre los precios y el stock en la plataforma oficial antes de pedir.",
    ],
    sections: [
      {
        heading: "Paso 1: abre y revisa las categorías",
        paragraphs: [
          "El spreadsheet está organizado por categorías, así que tu primera tarea es repasar la lista y encontrar el grupo que coincide con lo que quieres. Las categorías suelen incluir zapatos, sudaderas, camisetas, chaquetas, pantalones, gorros, bolsos, cinturones y accesorios.",
          "Tómate un momento para observar toda la estructura antes de profundizar. Entender cómo se agrupan los productos te ayudará a navegar mucho más rápido.",
        ],
      },
      {
        heading: "Paso 2: elige una categoría y anota las referencias",
        paragraphs: [
          "Una vez elegida la categoría, anota las referencias o nombres de productos que te interesen. Son los identificadores que usarás para localizar el artículo exacto en el mercado.",
        ],
        items: [
          "Anota o copia la referencia de cada artículo.",
          "Guarda una lista corta para comparar opciones una al lado de otra.",
          "Presta atención a las notas de talla y calidad si están incluidas.",
        ],
      },
      {
        heading: "Paso 3: compara vendedores",
        paragraphs: [
          "El spreadsheet puede incluir información del vendedor que te ayude a decidir a quién comprar. Comparar vendedores en fiabilidad, respuesta y precisión del producto puede ahorrarte problemas posteriores.",
        ],
      },
      {
        heading: "Paso 4: planifica el envío y el QC",
        paragraphs: [
          "Antes de pedir, comprueba los métodos de envío disponibles para tu destino y decide si quieres fotos de control de calidad. Las fotos QC te permiten confirmar que un artículo se ve correcto antes de que salga del almacén, algo especialmente útil para ropa y calzado.",
        ],
      },
      {
        heading: "Paso 5: verifica y pide",
        paragraphs: [
          "Por último, verifica los precios actuales, la disponibilidad y la reputación del vendedor en la plataforma oficial. Los precios y el stock cambian con frecuencia, así que trata el spreadsheet como una guía y no como un catálogo en vivo. Una vez que todo cuadre, puedes pedir con confianza.",
        ],
      },
    ],
    faq: [
      {
        q: "¿Necesito registrarme antes de usar el spreadsheet?",
        a: "El spreadsheet en sí es solo un documento de referencia. Para pedir de verdad, necesitarás una cuenta de Basetao en la plataforma oficial.",
      },
      {
        q: "¿Con qué frecuencia se actualiza el spreadsheet?",
        a: "La frecuencia varía según la fuente. Compara siempre con la plataforma en vivo para los precios y la disponibilidad más actuales.",
      },
      {
        q: "¿Cuál es la forma más fácil de evitar errores?",
        a: "Sigue los pasos en orden, anota las referencias y verifica cada detalle en la plataforma oficial antes de pagar.",
      },
      {
        q: "¿Debería usar fotos QC?",
        a: "Para ropa y calzado, las fotos QC suelen valer la pena porque te permiten confirmar talla y apariencia antes del envío internacional.",
      },
    ],
    conclusion:
      "Usar el Basetao Spreadsheet se reduce a una rutina simple: revisar categorías, anotar referencias, comparar vendedores, planificar envío y QC, y verificar antes de pedir. Sigue estos pasos y pasarás menos tiempo buscando y más tiempo tomando decisiones con confianza.",
  },

  "basetao-spreadsheet-sellers": {
    slug: "basetao-spreadsheet-sellers",
    title: "Guía de vendedores de Basetao Spreadsheet: cómo compararlos",
    description:
      "Aprende a evaluar y comparar vendedores en el Basetao Spreadsheet para elegir opciones fiables y evitar errores comunes de compra.",
    intro: [
      "Elegir al vendedor correcto es una de las decisiones más importantes al comprar a través de Basetao. Un buen vendedor te ahorra tiempo y dinero, mientras que uno malo puede llevarte a artículos equivocados, retrasos o devoluciones difíciles.",
      "Esta guía explica qué buscar al comparar vendedores en el Basetao Spreadsheet y cómo tomar una decisión con la que te sientas tranquilo.",
    ],
    takeaways: [
      "Compara vendedores por precisión, respuesta y fiabilidad, no solo por precio.",
      "Comprueba si un vendedor ofrece referencias claras y detalles de talla.",
      "Usa fotos QC y reseñas cuando estén disponibles para reducir el riesgo.",
      "Verifica el estado actual del vendedor en la plataforma oficial antes de pedir.",
    ],
    sections: [
      {
        heading: "Por qué importa la elección del vendedor",
        paragraphs: [
          "Distintos vendedores pueden ofrecer el mismo producto con distintos niveles de calidad, precio y disponibilidad. El vendedor que elijas afecta lo que recibes, cuánto tarda y lo fácil que es resolver cualquier problema.",
        ],
      },
      {
        heading: "Qué comparar",
        paragraphs: [
          "Al revisar vendedores en el spreadsheet, céntrate en los detalles que realmente afectan tu pedido.",
        ],
        items: [
          "Precisión del producto — ¿coincide el anuncio con el artículo real?",
          "Referencias claras — ¿son los identificadores lo bastante específicos?",
          "Respuesta y comunicación — ¿es fácil contactar al vendedor?",
          "Notas de talla y calidad — ¿hay observaciones útiles?",
          "Reseñas y valoraciones — ¿qué dicen otros compradores?",
        ],
      },
      {
        heading: "Señales de un vendedor fiable",
        paragraphs: [
          "Los vendedores fiables tienden a ofrecer información consistente y detallada y a responder con claridad. Busca vendedores que listan referencias precisas, incluyen orientación de tallas y tienen un historial descrito positivamente por otros compradores.",
        ],
      },
      {
        heading: "Errores comunes que evitar",
        paragraphs: [
          "Los errores más comunes vienen de perseguir el precio más bajo sin comprobar al vendedor. Esto puede llevar a tallas equivocadas, menor calidad o artículos que difieren de las fotos.",
        ],
        items: [
          "Elegir solo por precio sin comparar detalles.",
          "Saltarse las fotos QC en artículos donde el ajuste y la apariencia importan.",
          "Ignorar las notas de talla y pedir la talla equivocada.",
          "No verificar el estado actual del vendedor antes de pagar.",
        ],
      },
    ],
    faq: [
      {
        q: "¿Cómo sé si un vendedor es fiable?",
        a: "Busca referencias precisas, detalles de talla claros, comunicación receptiva y comentarios positivos de otros compradores.",
      },
      {
        q: "¿Debo elegir siempre al vendedor más barato?",
        a: "No necesariamente. El precio más bajo puede venir con menor calidad o peor soporte, así que compara el panorama completo antes de decidir.",
      },
      {
        q: "¿Ayudan las fotos QC a elegir vendedor?",
        a: "Sí, las fotos QC confirman que el artículo real coincide con el anuncio antes de enviarse, lo que reduce el riesgo con vendedores desconocidos.",
      },
      {
        q: "¿Puedo cambiar de vendedor después de pedir?",
        a: "Las políticas varían, así que consulta las reglas actuales en la plataforma oficial si necesitas cambiar algo.",
      },
    ],
    conclusion:
      "La elección del vendedor puede marcar tu experiencia con Basetao. Al comparar precisión, comunicación y fiabilidad en lugar de solo el precio, y usar fotos QC donde importa, compras con mucha más confianza.",
  },

  "basetao-spreadsheet-qc": {
    slug: "basetao-spreadsheet-qc",
    title: "Guía QC de Basetao Spreadsheet: fotos de control de calidad explicadas",
    description:
      "Entiende cómo funcionan las fotos de control de calidad (QC) de Basetao, cuándo pedirlas y cómo te ayudan a confirmar productos antes del envío.",
    intro: [
      "Las fotos de control de calidad son una de las funciones más valiosas al comprar a través de Basetao. Te muestran el artículo real que estás comprando antes de enviarlo internacionalmente, lo que puede evitar errores costosos.",
      "Esta guía explica qué son las fotos QC, cuándo usarlas y cómo interpretar lo que ves.",
    ],
    takeaways: [
      "Las fotos QC muestran el artículo real en el almacén antes del envío.",
      "Son especialmente útiles para ropa, calzado y artículos donde el ajuste y la apariencia importan.",
      "Revisa las fotos con cuidado por talla, color y defectos antes de aprobar el envío.",
      "Pide QC con antelación para tener tiempo de resolver problemas.",
    ],
    sections: [
      {
        heading: "¿Qué son las fotos QC?",
        paragraphs: [
          "Las fotos de control de calidad son imágenes tomadas de tu artículo después de que llega al almacén de Basetao y antes de empaquetarse para el envío internacional. Te dan la oportunidad de confirmar que lo recibido coincide con lo pedido.",
        ],
      },
      {
        heading: "Cuándo pedir fotos QC",
        paragraphs: [
          "Las fotos QC son más útiles cuando la apariencia, la talla o el estado de un artículo importan mucho.",
        ],
        items: [
          "Ropa y calzado, donde la talla y el color son fáciles de equivocar.",
          "Artículos con marca visible o detalles de diseño específicos.",
          "Productos frágiles o de alto valor.",
          "Primeros pedidos a un vendedor que no has usado antes.",
        ],
      },
      {
        heading: "Cómo revisar las fotos QC",
        paragraphs: [
          "Cuando recibas tus fotos QC, revísalas con cuidado en lugar de mirarlas de pasada. Compara el artículo con el anuncio y busca cualquier cosa que no coincida.",
        ],
        items: [
          "Comprueba la etiqueta de talla y las medidas si se proporcionan.",
          "Compara colores y detalles de diseño con el anuncio original.",
          "Busca defectos, manchas o daños visibles.",
          "Confirma que llegó la cantidad y variante correctas.",
        ],
      },
      {
        heading: "Qué hacer si algo está mal",
        paragraphs: [
          "Si las fotos QC revelan un problema, contacta con el soporte de Basetao antes de aprobar el envío. En muchos casos, los problemas se resuelven con el vendedor o el artículo se cambia mientras sigue en el almacén, algo mucho más fácil que lidiar con un problema después del envío internacional.",
        ],
      },
    ],
    faq: [
      {
        q: "¿Son gratis las fotos QC?",
        a: "Las políticas varían y algunos planes pueden cobrar una pequeña tarifa. Consulta las condiciones actuales en la plataforma oficial.",
      },
      {
        q: "¿Cuántas fotos recibiré?",
        a: "El número puede variar según el servicio y el artículo. Confirma qué se incluye al pedir el QC.",
      },
      {
        q: "¿Pueden retrasar mi pedido las fotos QC?",
        a: "Añaden un pequeño paso antes del envío, pero el retraso suele ser menor frente al riesgo de recibir un artículo equivocado.",
      },
      {
        q: "¿Y si el artículo sale defectuoso en las fotos?",
        a: "Contacta con el soporte de inmediato y no apruebes el envío hasta que se resuelva el problema.",
      },
    ],
    conclusion:
      "Las fotos QC son una forma simple pero poderosa de protegerte al comprar a través de Basetao. Pídelas para artículos donde el ajuste y la apariencia importan, revísalas con cuidado y actúa rápido si algo se ve mal.",
  },

  "basetao-spreadsheet-shipping": {
    slug: "basetao-spreadsheet-shipping",
    title: "Guía de envío de Basetao Spreadsheet: métodos, tiempo y seguimiento",
    description:
      "Una guía clara sobre los métodos de envío de Basetao, cuánto tarda el envío y cómo rastrear tu pedido desde el almacén hasta tu puerta.",
    intro: [
      "El envío es una de las partes del proceso de Basetao que los compradores nuevos encuentran más confusa. Hay varios métodos, plazos diferentes y varios factores que afectan el coste y la velocidad.",
      "Esta guía explica los conceptos básicos del envío de Basetao para que puedas elegir un método que se ajuste a tu presupuesto y a la rapidez con que necesitas el artículo.",
    ],
    takeaways: [
      "El método, el peso, el volumen y el destino afectan el coste y la velocidad.",
      "Los métodos más rápidos cuestan más pero llegan antes; los lentos ahorran dinero.",
      "La consolidación combina varios artículos en un envío para reducir el coste.",
      "Verifica siempre las tarifas y plazos actuales antes de pedir.",
    ],
    sections: [
      {
        heading: "Cómo funciona el envío de Basetao",
        paragraphs: [
          "Después de que tus artículos llegan al almacén y pasan el QC, se empaquetan y se envían a tu dirección. Basetao ofrece una gama de métodos, desde opciones económicas más lentas hasta opciones exprés que llegan en cuestión de días.",
        ],
      },
      {
        heading: "Métodos de envío comunes",
        paragraphs: [
          "Las opciones exactas pueden cambiar, pero la mayoría de compradores se encuentra con unas pocas categorías amplias.",
        ],
        items: [
          "Líneas económicas — la opción más barata, con plazos más largos.",
          "Líneas estándar — un equilibrio entre coste y velocidad.",
          "Líneas exprés — entrega más rápida a un precio más alto.",
          "Líneas especializadas — opciones para destinos o tipos de producto concretos.",
        ],
      },
      {
        heading: "Consolidación y reempaquetado",
        paragraphs: [
          "Si compras varios artículos de distintos vendedores, Basetao puede consolidarlos en un solo paquete. Esto reduce el número de envíos y a menudo el coste total. El reempaquetado también puede reducir el volumen, lo que importa porque algunos métodos cobran por peso volumétrico.",
        ],
      },
      {
        heading: "Rastrear tu pedido",
        paragraphs: [
          "Una vez enviado tu paquete, recibirás un número de seguimiento. Las actualizaciones pueden tardar uno o dos días en aparecer y pueden pausarse durante el tránsito internacional. Guarda tu número de seguimiento y consulta la plataforma oficial para el estado más reciente.",
        ],
      },
      {
        heading: "Qué afecta al tiempo de envío",
        paragraphs: [
          "El tiempo de tránsito depende del método elegido, tu destino y el despacho de aduanas. Las temporadas altas también pueden añadir retrasos, así que planifica con antelación si necesitas un artículo para una fecha concreta.",
        ],
      },
    ],
    faq: [
      {
        q: "¿Cuánto tarda el envío de Basetao?",
        a: "Depende del método y el destino. Las opciones exprés llegan más rápido, mientras que las líneas económicas tardan más. Consulta las estimaciones actuales antes de pedir.",
      },
      {
        q: "¿Qué es la consolidación?",
        a: "La consolidación combina varios artículos en un envío, lo que puede reducir el coste total de envío.",
      },
      {
        q: "¿Cómo se calcula el coste de envío?",
        a: "El coste suele basarse en el peso real o volumétrico, el destino y el método que elijas.",
      },
      {
        q: "¿Puedo rastrear mi paquete?",
        a: "Sí, recibirás un número de seguimiento tras el envío. Las actualizaciones pueden pausarse durante el tránsito internacional.",
      },
    ],
    conclusion:
      "Entender el envío de Basetao te ayuda a elegir el equilibrio correcto entre coste y velocidad. Considera la consolidación para varios artículos, vigila el peso volumétrico y confirma siempre las tarifas y plazos actuales antes de pedir.",
  },

  "basetao-shipping-cost": {
    slug: "basetao-shipping-cost",
    title: "Coste de envío de Basetao: guía completa de factores de precio",
    description:
      "Aprende qué afecta al coste de envío de Basetao, del peso y el volumen al destino y el método, y cómo estimar tu total antes de pedir.",
    intro: [
      "El coste de envío de Basetao rara vez es una cifra fija. Varios factores determinan lo que pagas, y entenderlos te ayuda a evitar sorpresas al pagar.",
      "Esta guía desglosa los principales factores de coste y ofrece formas prácticas de mantener tu factura de envío bajo control.",
    ],
    takeaways: [
      "El peso, el volumen, el destino y el método son los principales factores de coste.",
      "El peso volumétrico puede hacer que artículos voluminosos cuesten más que los pesados.",
      "La consolidación y el reempaquetado suelen reducir el coste total.",
      "Las tarifas cambian, así que verifica los precios actuales antes de pedir.",
    ],
    sections: [
      {
        heading: "Los principales factores que afectan al coste",
        paragraphs: [
          "El coste de envío se calcula a partir de varios datos. Conocerlos te ayuda a predecir y reducir lo que pagas.",
        ],
        items: [
          "Peso real — el peso físico del paquete.",
          "Peso volumétrico — el espacio que ocupa el paquete.",
          "Destino — enviar a algunas regiones cuesta más.",
          "Método de envío — las opciones rápidas cuestan más.",
          "Valor declarado — puede afectar tarifas y seguro.",
          "Servicios extra — como reempaquetado o seguro.",
        ],
      },
      {
        heading: "Peso real vs peso volumétrico",
        paragraphs: [
          "Los transportistas suelen cobrar el mayor de los dos: el peso real o el volumétrico. El peso volumétrico se calcula a partir de las dimensiones del paquete, así que un artículo ligero pero voluminoso puede costar lo mismo que uno pesado y compacto. Reempaquetar para reducir el volumen puede, por tanto, ahorrar dinero de verdad.",
        ],
      },
      {
        heading: "Cómo reducir el coste de envío",
        paragraphs: [
          "Hay varias formas prácticas de reducir tu factura sin comprometer demasiado la velocidad.",
        ],
        items: [
          "Consolida varios artículos en un solo envío.",
          "Pide reempaquetado para reducir el volumen.",
          "Elige un método más lento cuando el tiempo no sea crítico.",
          "Compara los métodos disponibles para tu destino.",
        ],
      },
      {
        heading: "Estimar tu total",
        paragraphs: [
          "Antes de pedir, usa la calculadora de envío oficial de Basetao o la herramienta de estimación si está disponible, y confirma las tarifas para tu destino y método. Recuerda que los recargos estacionales y los ajustes de combustible pueden cambiar los precios, así que trata cualquier estimación como aproximada.",
        ],
      },
    ],
    faq: [
      {
        q: "¿Cuánto cuesta el envío de Basetao?",
        a: "No hay una respuesta única porque el coste depende del peso, el volumen, el destino y el método. Consulta las tarifas actuales para tu situación.",
      },
      {
        q: "¿Por qué un paquete ligero cuesta tanto?",
        a: "Puede cobrarse por peso volumétrico, es decir, el paquete ocupa más espacio del que sugiere su peso real.",
      },
      {
        q: "¿Puede la consolidación ahorrar dinero?",
        a: "Sí, combinar artículos en un envío suele reducir el coste total frente a enviar cada artículo por separado.",
      },
      {
        q: "¿Cambian los precios de envío?",
        a: "Sí, las tarifas pueden cambiar por demanda estacional, ajustes de combustible y precios del transportista. Verifica siempre antes de pedir.",
      },
    ],
    conclusion:
      "El coste de envío de Basetao depende del peso, el volumen, el destino y el método. Al entender el peso volumétrico y usar consolidación y reempaquetado, puedes mantener los costes razonables y evitar sorpresas.",
  },

  "basetao-warehouse": {
    slug: "basetao-warehouse",
    title: "Guía del almacén de Basetao: almacenamiento, QC y consolidación",
    description:
      "Aprende cómo funciona el almacén de Basetao, desde la recepción y el almacenamiento hasta las fotos QC, la consolidación y la preparación del envío.",
    intro: [
      "El almacén de Basetao es el centro donde tus artículos se reciben, inspeccionan, almacenan y finalmente se preparan para el envío internacional. Entender cómo funciona te ayuda a usar el servicio de forma más eficaz.",
      "Esta guía recorre cada etapa del proceso del almacén para que sepas qué ocurre después de hacer tu pedido.",
    ],
    takeaways: [
      "El almacén recibe, almacena e inspecciona tus artículos tras la compra.",
      "Las fotos QC se toman en el almacén para que verifiques los productos.",
      "La consolidación y el reempaquetado ocurren aquí antes del envío.",
      "El almacenamiento suele ser gratis por un periodo limitado; después pueden aplicarse tarifas.",
    ],
    sections: [
      {
        heading: "Qué hace el almacén",
        paragraphs: [
          "Después de que un vendedor envía tu artículo, este llega al almacén de Basetao. Allí, el personal lo registra, lo almacena y lo deja disponible para inspección, consolidación y embalaje final.",
        ],
      },
      {
        heading: "El proceso del almacén paso a paso",
        paragraphs: [
          "El recorrido de tu artículo por el almacén sigue un camino predecible.",
        ],
        items: [
          "Pedido realizado — el vendedor envía tu artículo.",
          "El almacén recibe y registra el artículo.",
          "Almacenamiento — el artículo espera más instrucciones.",
          "QC — fotos opcionales confirman estado y detalles.",
          "Consolidación — se combinan varios artículos.",
          "Preparación del envío — embalaje y generación de etiqueta.",
        ],
      },
      {
        heading: "Periodos de almacenamiento y tarifas",
        paragraphs: [
          "La mayoría de servicios de almacén ofrecen un periodo de almacenamiento gratis, tras el cual pueden aplicarse tarifas diarias o mensuales. Si planeas comprar artículos durante varios días antes del envío, comprueba cuánto dura el almacenamiento gratis para consolidar dentro de ese plazo.",
        ],
      },
      {
        heading: "Consolidación y reempaquetado",
        paragraphs: [
          "La consolidación combina artículos de distintos pedidos en un paquete. El reempaquetado elimina el embalaje sobrante para reducir volumen y coste de envío. Ambos se gestionan en el almacén y son útiles cuando compras a varios vendedores.",
        ],
      },
    ],
    faq: [
      {
        q: "¿Es gratis el almacenamiento?",
        a: "Suele haber un periodo de almacenamiento gratis, tras el cual pueden aplicarse tarifas. Consulta la política actual en la plataforma oficial.",
      },
      {
        q: "¿Qué es la consolidación?",
        a: "La consolidación combina varios artículos en un solo envío, lo que puede reducir el coste total de envío.",
      },
      {
        q: "¿Dónde se toman las fotos QC?",
        a: "Las fotos QC se toman en el almacén tras la llegada de tu artículo y antes de su envío.",
      },
      {
        q: "¿Puedo almacenar artículos mientras sigo comprando?",
        a: "Sí, el almacén puede guardar artículos durante un periodo para que consolides un pedido mayor antes del envío.",
      },
    ],
    conclusion:
      "El almacén de Basetao es más que almacenamiento. Es donde tus artículos se inspeccionan, fotografían, consolidan y preparan para el envío. Entender cada paso te ayuda a planificar pedidos y ahorrar en envíos.",
  },

  "basetao-coupons": {
    slug: "basetao-coupons",
    title: "Guía de cupones y códigos de descuento de Basetao: cómo ahorrar",
    description:
      "Aprende cómo funcionan los cupones y códigos de descuento de Basetao, dónde encontrar ofertas actuales y cómo verificar un código antes de usarlo.",
    intro: [
      "Los cupones y códigos de descuento pueden reducir el coste de comprar a través de Basetao, pero no siempre están disponibles y tienen reglas. Saber cómo funcionan te ayuda a usarlos con honestidad y eficacia.",
      "Esta guía explica dónde pueden aparecer las ofertas, cómo comprobar si un código es válido y a qué prestar atención.",
    ],
    takeaways: [
      "Los cupones y códigos pueden aparecer periódicamente, pero la disponibilidad cambia a menudo.",
      "Verifica siempre que un código siga siendo válido antes de confiar en él.",
      "Las ofertas pueden tener restricciones de elegibilidad, caducidad y gasto mínimo.",
      "No compartas ni uses códigos de sitios de terceros no fiables.",
    ],
    sections: [
      {
        heading: "Cómo funcionan los cupones de Basetao",
        paragraphs: [
          "Un cupón o código de descuento es una cadena corta o una oferta que reduce el coste de un servicio, como un porcentaje de descuento en una tarifa o un descuento en el envío. Los códigos suelen introducirse en un punto concreto del pago o del área de cuenta.",
        ],
      },
      {
        heading: "Dónde pueden aparecer las ofertas",
        paragraphs: [
          "Los descuentos no siempre están activos, pero cuando lo están, pueden aparecer en unos pocos lugares comunes.",
        ],
        items: [
          "El sitio oficial de Basetao y el panel de cuenta.",
          "Boletines o anuncios oficiales.",
          "Promociones de temporada y eventos especiales.",
          "Canales oficiales de redes sociales.",
        ],
      },
      {
        heading: "Cómo verificar un código",
        paragraphs: [
          "Antes de confiar en un cupón, comprueba que sigue siendo válido y que se aplica a lo que compras.",
        ],
        items: [
          "Confirma que el código proviene de una fuente oficial.",
          "Comprueba la fecha de caducidad.",
          "Lee los requisitos de elegibilidad y gasto mínimo.",
          "Aplícalo al pagar y confirma que aparece el descuento.",
        ],
      },
      {
        heading: "Qué evitar",
        paragraphs: [
          "Evita códigos de sitios de terceros no fiables que prometen ahorros garantizados. Pueden estar obsoletos, ser inválidos o engañosos. Limítate a fuentes oficiales y verifica cada código antes de usarlo.",
        ],
      },
    ],
    faq: [
      {
        q: "¿Tiene Basetao cupones activos?",
        a: "Las ofertas pueden aparecer de vez en cuando, pero la disponibilidad cambia. Consulta la plataforma oficial para lo más reciente.",
      },
      {
        q: "¿Cómo uso un código de descuento?",
        a: "Introduce el código en el campo correspondiente al pagar o en tu cuenta, y confirma que se aplica el descuento.",
      },
      {
        q: "¿Caducan los códigos?",
        a: "Sí, la mayoría tienen fecha de caducidad y condiciones concretas, así que verifícalos antes de usarlos.",
      },
      {
        q: "¿Puedo combinar varios códigos?",
        a: "Normalmente no. Consulta las condiciones de cada oferta, ya que la mayoría no se pueden acumular.",
      },
    ],
    conclusion:
      "Los cupones y códigos de Basetao pueden ofrecer ahorros reales cuando están disponibles. La clave es usar fuentes oficiales, verificar cada código y entender las condiciones para no confiar nunca en una oferta caducada o inválida.",
  },

  "is-basetao-legit": {
    slug: "is-basetao-legit",
    title: "¿Es legítimo Basetao? Guía de seguridad y confianza",
    description:
      "Una mirada equilibrada sobre si Basetao es legítimo, cómo evaluar la seguridad y los pasos que puedes dar para protegerte al comprar.",
    intro: [
      "Si estás considerando Basetao por primera vez, es natural preguntarse si el servicio es legítimo y seguro. La respuesta corta es que Basetao es un agente de compras conocido, pero la seguridad siempre depende de lo cuidadoso que seas al comprar.",
      "Esta guía explica cómo evaluar la confianza, qué riesgos vigilar y los pasos prácticos que puedes dar para protegerte.",
    ],
    takeaways: [
      "Basetao es un agente de compras reconocido, pero siempre debes verificar los detalles por ti mismo.",
      "Comprueba la seguridad del sitio, los métodos de pago y las políticas de reembolso antes de comprometerte.",
      "Investiga a vendedores y productos en lugar de confiar solo en el servicio.",
      "Protege tu cuenta y tu información personal en todo momento.",
    ],
    sections: [
      {
        heading: "¿Es legítimo Basetao?",
        paragraphs: [
          "Basetao es un agente de compras de larga trayectoria que muchos compradores internacionales usan para comprar productos de mercados chinos. Ofrece realización de pedidos, almacenamiento, QC y envío. Como con cualquier agente, la legitimidad del servicio es independiente de la fiabilidad de vendedores y productos, por eso importa la verificación personal.",
        ],
      },
      {
        heading: "Cómo evaluar la seguridad",
        paragraphs: [
          "En lugar de buscar un simple sí o no, evalúa a un agente de compras según varios factores concretos.",
        ],
        items: [
          "Seguridad del sitio — comprueba HTTPS y un dominio legítimo.",
          "Métodos de pago — usa opciones seguras y rastreables.",
          "Política de reembolso — entiende cómo funcionan reembolsos y devoluciones.",
          "Información del vendedor — verifica a los vendedores a los que planeas comprar.",
          "Detalles del producto — confirma la exactitud antes de pedir.",
          "Seguridad de la cuenta — usa una contraseña fuerte y única.",
        ],
      },
      {
        heading: "Señales de alerta a vigilar",
        paragraphs: [
          "Ten cuidado si sientes presión para pagar rápido, condiciones de reembolso poco claras, solicitudes de pago sospechosas o vendedores que evitan preguntas. Los servicios y vendedores legítimos suelen responder preguntas y ofrecer información clara.",
        ],
      },
      {
        heading: "Proteger tu información",
        paragraphs: [
          "Usa una contraseña fuerte y única para tu cuenta, evita compartir credenciales y ten cuidado con la información personal. Introduce datos sensibles solo en el sitio oficial, nunca a través de enlaces o mensajes no solicitados.",
        ],
      },
    ],
    faq: [
      {
        q: "¿Es seguro usar Basetao?",
        a: "Basetao es un servicio reconocido, pero la seguridad depende de verificar vendedores, productos y políticas por ti mismo antes de pedir.",
      },
      {
        q: "¿Cómo sé si un vendedor es de confianza?",
        a: "Busca anuncios precisos, comunicación clara y comentarios positivos de otros compradores.",
      },
      {
        q: "¿Qué métodos de pago acepta Basetao?",
        a: "Las opciones de pago pueden cambiar, así que consulta la plataforma oficial para la lista actual.",
      },
      {
        q: "¿Qué hago si algo sale mal?",
        a: "Contacta con el soporte de Basetao de inmediato y guarda registros de tu pedido, pagos y comunicación.",
      },
    ],
    conclusion:
      "Basetao es un agente de compras legítimo, pero la confianza se construye mediante una verificación cuidadosa, no con suposiciones. Al comprobar la seguridad, el pago, las políticas y los vendedores por ti mismo, compras con mucha más confianza.",
  },

  "basetao-review": {
    slug: "basetao-review",
    title: "Reseña de Basetao: lo que debes saber antes de usarlo",
    description:
      "Una reseña honesta y equilibrada de Basetao que cubre cómo funciona, sus puntos fuertes y limitaciones, y para quién es más adecuado.",
    intro: [
      "Esta reseña ofrece una mirada equilibrada a Basetao, un agente de compras que ayuda a los compradores internacionales a comprar productos de mercados chinos. Cubre cómo funciona el servicio, qué hace bien, dónde tiene limitaciones y quién se beneficia más.",
      "El objetivo es darte una visión honesta para que decidas si Basetao se ajusta a tus necesidades, sin exageraciones de marketing ni afirmaciones sin fundamento.",
    ],
    takeaways: [
      "Basetao actúa como capa intermedia para pedidos, almacenamiento, QC y envío.",
      "Es más útil para compradores que quieren ayuda con productos de mercados chinos.",
      "El servicio tiene puntos fuertes claros pero exige una verificación personal cuidadosa.",
      "La idoneidad depende de tu presupuesto, destino y qué planeas comprar.",
    ],
    sections: [
      {
        heading: "Cómo funciona Basetao",
        paragraphs: [
          "Basetao realiza pedidos en tu nombre, recibe artículos en su almacén, ofrece fotos de control de calidad opcionales y envía tus compras internacionalmente. El Basetao Spreadsheet es un recurso complementario que organiza productos y referencias para facilitar la investigación.",
        ],
      },
      {
        heading: "Puntos fuertes",
        paragraphs: [
          "Los principales puntos fuertes de Basetao provienen de centralizar un proceso que de otro modo sería fragmentado y lento.",
        ],
        items: [
          "Pedidos y almacenamiento centralizados.",
          "Fotos QC opcionales antes del envío.",
          "Consolidación de varios artículos en un envío.",
          "Un spreadsheet estructurado para investigar productos.",
        ],
      },
      {
        heading: "Limitaciones",
        paragraphs: [
          "Como cualquier servicio, Basetao tiene limitaciones que conviene conocer antes de empezar.",
        ],
        items: [
          "Los costes de envío varían y pueden acumularse.",
          "El servicio no garantiza la calidad del vendedor ni del producto.",
          "Los precios y la disponibilidad cambian con frecuencia.",
          "Algunos procesos requieren paciencia y atención al detalle.",
        ],
      },
      {
        heading: "Para quién puede ser adecuado",
        paragraphs: [
          "Basetao suele convenir a compradores que quieren ayuda para adquirir productos de mercados chinos, sobre todo quienes compran ropa, calzado y accesorios y valoran las fotos QC y la consolidación. Es menos ideal para quienes necesitan entrega inmediata o prefieren gestionar cada paso por su cuenta.",
        ],
      },
    ],
    faq: [
      {
        q: "¿Vale la pena Basetao?",
        a: "Puede valerlo, sobre todo si compras varios artículos y valoras las fotos QC y la consolidación. El valor depende de tus necesidades concretas.",
      },
      {
        q: "¿Cuáles son las desventajas?",
        a: "Los costes de envío varían, la calidad depende de los vendedores y el proceso requiere algo de paciencia.",
      },
      {
        q: "¿Quién debería usar Basetao?",
        a: "Compradores que quieren ayuda para pedir en mercados chinos y valoran el almacenamiento, el QC y la consolidación.",
      },
      {
        q: "¿Garantiza Basetao la calidad?",
        a: "No. El servicio no garantiza la calidad del vendedor ni del producto, así que debes verificar los artículos por ti mismo.",
      },
    ],
    conclusion:
      "Basetao es una herramienta práctica para compradores que quieren una forma centralizada de pedir en mercados chinos, con funciones útiles como fotos QC y consolidación. No es perfecto y exige verificación personal, pero para el comprador adecuado simplifica un proceso complejo.",
  },
};